import { Link } from '@features/link'
import type { Section } from '@st/sdk'
import {
  Banner,
  Button,
  CloseIcon,
  Dialog,
  DialogButtons,
  Modal,
  Spinner,
  ToggleSwitch
} from '@st/theme'
import { match } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { selOrganizerSettings } from './folder-organizer-settings-module'

export function FolderOrganizerSettingsDialog() {
  const { send } = useProcess('folderOrganizerSettings')
  const state = useSelect('folderOrganizerSettings', (state) => state)
  const model = useSelect('folderOrganizerSettings', selOrganizerSettings)
  if (state.status == 'idle') return null

  const close = () => send({ type: 'close' })
  const saving = state.status == 'saving'
  const { edited } = model

  return (
    <Modal isOpen={true} onClose={close}>
      <Dialog
        className="gap-0 sm:max-w-3xl sm:rounded-lg [&>div:first-child]:mb-4"
        title={
          <span className="flex items-center justify-between gap-4 text-xl font-semibold">
            Organizer sections
            <Button variant="icon" aria-label="Close settings" onClick={close} disabled={saving}>
              <CloseIcon className="size-4" />
            </Button>
          </span>
        }
        subtitle={
          state.status != 'loading' && (
            <span className="block text-sm leading-5">
              {match(state.settings.source)
                .with('organization', () => (
                  <>
                    These settings come from{' '}
                    <Link
                      href={{ name: 'client_organizer_settings' }}
                      onClick={close}
                      className="text-indigo-600 underline"
                    >
                      Settings → Client Organizers → {state.returnType}
                    </Link>
                    . You can override them below.
                  </>
                ))
                .with(
                  'folder',
                  () =>
                    'These settings have been overridden. Changes here only affect this client organizer.'
                )
                .exhaustive()}
            </span>
          )
        }
        loading={state.status == 'loading'}
        buttons={
          <DialogButtons
            leftButton={
              <div role="status" className="flex items-center gap-2 text-sm text-gray-700">
                {edited && (
                  <>
                    <span className="size-2 rounded-full bg-yellow-500" />
                    Unsaved changes
                  </>
                )}
              </div>
            }
          >
            <Button variant="subtle" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!model.canSave}
              leadingIcon={saving && <Spinner className="size-4" />}
              onClick={() => send({ type: 'save' })}
            >
              Save
            </Button>
          </DialogButtons>
        }
      >
        {state.status != 'loading' && (
          <>
            {model.willOverride && (
              <Banner variant="info" role="status" className="mb-4 text-left text-sm">
                Saving will override these settings only for this client organizer.
              </Banner>
            )}
            <fieldset disabled={saving} className="flex flex-col gap-3">
              {model.sections.map(([id, section]) => {
                const { label, description } = sectionCopy[section.type]
                return (
                  <div
                    key={section.type}
                    className="flex items-center justify-between gap-4 rounded-lg border border-gray-300 p-6"
                  >
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{label}</h3>
                      <p className="mt-1 text-base text-gray-700">{description}</p>
                    </div>
                    <ToggleSwitch
                      aria-label={label}
                      value={section.enabled}
                      onChange={(enabled) => send({ type: 'change', section: id, enabled })}
                    />
                  </div>
                )
              })}
            </fieldset>
          </>
        )}
      </Dialog>
    </Modal>
  )
}

const sectionCopy = {
  engagement_letter: {
    label: 'Engagement letter',
    description: 'Show your engagement letter for the client to review and sign.'
  },
  questionnaire: {
    label: 'Questionnaire',
    description: 'Show the questionnaire in the client organizer.'
  },
  documents: {
    label: 'Document uploads',
    description: 'Show the document request list and upload section on the organizer dashboard.'
  }
} satisfies Record<Section['type'], { label: string; description: string }>
